import CrudStoreBuilder from "../common/CrudStoreBuilder";
import bookingService from "@/services/salles/BookingService";
import notif from "@/services/common/NotificationHelperService";
import i18n from "@/i18n";
import { DateTime, Interval } from "luxon";

// retrieve the corresponding resource from shared stores instead of returning a new resource object
function getStoredResource(rootGetters, resource) {
  switch (resource.type) {
    case "room":
      return rootGetters["roomModule/getRoomById"](resource.id);
    case "desk":
      return rootGetters["deskModule/getDeskById"](resource.id);
    case "equipment":
      return rootGetters["equipmentModule/getEquipmentById"](resource.id);
  }
  throw new Error("invalid resource type");
}

function getStoredBooking(rootGetters, bookingId) {
  return rootGetters["bookingModule/getBookingById"](bookingId);
}

function getCurrentUser(rootGetters) {
  return rootGetters["userModule/getCurrentUser"];
}

function addBookingImpl({ commit, dispatch }, booking, userId) {
  const bookingToSave = {
    id: 0,
    version: 0,
    dateStart: booking.dateStart.toUTC().toISO(),
    dateEnd: booking.dateEnd.toUTC().toISO(),
    userId: userId,
    centerId: booking.center.id,
    resources: [],
    numUsers: booking.numUsers,
    reason: booking.reason,
    complement: booking.complement,
  };
  for (const resource of booking.resources) {
    bookingToSave.resources.push({
      id: resource.id,
      type: resource.type,
    });
  }

  return new Promise((resolve, reject) => {
    bookingService
      .save(bookingToSave)
      .then((response) => {
        dispatch("loadBooking", response.data).then((x) => {
          commit("ADD_NEW_BOOKING", x);
          resolve(x);
        });
        notif.showSuccess(dispatch, i18n.t("messageStores.bookingAddSuccess"));
      })
      .catch((e) => {
        reject(e);
        notif.showError(dispatch, i18n.t("messageStores.bookingAddError"));
      });
  });
}

function _isWholeRoomBooking(booking) {
  if (!booking.resources?.length) return false;
  if (booking.resources.length === 1 && booking.resources[0].type === "room")
    return true;
  return (
    booking.resources.filter((x) => x.type === "room").length === 1 &&
    booking.resources.filter((x) => x.type === "desk").length === 0
  );
}

const actions = {
  loadBooking({ rootGetters }, booking) {
    const bookingEx = {
      ...booking,
      dateStart: DateTime.fromISO(booking.dateStart, { zone: "utc" }).toLocal(),
      dateEnd: DateTime.fromISO(booking.dateEnd, { zone: "utc" }).toLocal(),
      user: rootGetters["userModule/getUserById"](booking.userId),
      center: rootGetters["centerModule/getCenterById"](booking.centerId),
      resources: [],
    };
    bookingEx.interval = Interval.fromDateTimes(
      bookingEx.dateStart,
      bookingEx.dateEnd
    );
    for (const resource of booking.resources)
      bookingEx.resources.push(getStoredResource(rootGetters, resource));
    if (_isWholeRoomBooking(booking)) {
      const room = bookingEx.resources.find((x) => x.type === "room");
      bookingEx.resources.push(
        ...rootGetters["deskModule/getDesksByRoom"](room)
      );
    }
    delete bookingEx.version;
    delete bookingEx.centerId;
    return bookingEx;
  },

  addBookingForUser({ commit, dispatch }, booking) {
    return addBookingImpl({ commit, dispatch }, booking, booking.user.id);
  },

  addBooking({ rootGetters, commit, dispatch }, booking) {
    return addBookingImpl(
      { commit, dispatch },
      booking,
      getCurrentUser(rootGetters).id
    );
  },

  cancelBooking({ commit, dispatch }, booking) {
    return new Promise((resolve, reject) => {
      bookingService
        .cancelBooking(booking.id)
        .then((response) => {
          booking.cancelled = true;
          resolve(booking);
          notif.showSuccess(
            dispatch,
            i18n.t("messageStores.bookingsCancelSuccess")
          );
        })
        .catch((e) => {
          reject(e);
          notif.showError(
            dispatch,
            i18n.t("messageStores.bookingsCancelError")
          );
        });
    });
  },

  rebuildTimeslotsCache({ rootGetters, commit, dispatch }) {
    const bookings = rootGetters["bookingModule/getBookings"];
    const timeslots = {
      byRoom: new Map(),
      byCenter: new Map(),
    };
    for (const booking of bookings) cacheByTimeSlots(timeslots, booking);
    commit("SET_TIMESLOTS", timeslots);
  },
};

function makeIntervals30(date) {
  const intervals = [];

  for (let i = 0; i < 9; ++i) {
    intervals.push(makeInterval(date, i + 9, 0, i + 9, 30));
    intervals.push(makeInterval(date, i + 9, 30, i + 9 + 1, 0));
  }

  return intervals;
}

function cacheRoomBookingsByTimeSlots(timeslots, booking, resources) {
  const room = resources.rooms[0];

  if (!timeslots.byRoom.has(room))
    timeslots.byRoom.set(room, { byDay: new Map() });
  const byRoom = timeslots.byRoom.get(room);

  const center = room.center;

  if (!timeslots.byCenter.has(center))
    timeslots.byCenter.set(center, { byDay: new Map() });
  const byCenter = timeslots.byCenter.get(center);

  let dateBegin = booking.dateStart;
  while (dateBegin < booking.dateEnd) {
    const dateFormatted = dateBegin.toFormat("yyyy-MM-dd");
    if (!byRoom.byDay.has(dateFormatted)) {
      const slots = [];
      for (let i = 0; i < 18; ++i) slots.push({ any: false, desks: new Set() });
      byRoom.byDay.set(dateFormatted, {
        slots: slots,
      });
    }
    const byDay = byRoom.byDay.get(dateFormatted);

    if (!byCenter.byDay.has(dateFormatted)) {
      const slots = [];
      for (let i = 0; i < 18; ++i)
        slots.push({ any: false, equipments: new Set() });
      byCenter.byDay.set(dateFormatted, {
        slots: slots,
      });
    }
    const centerByDay = byCenter.byDay.get(dateFormatted);

    const intervals = makeIntervals30(dateBegin);
    for (let i = 0; i < intervals.length; ++i) {
      const whole = _isWholeRoomBooking(booking);

      if (intervals[i].overlaps(booking.interval)) {
        byDay.slots[i].any = true;
        byDay.slots[i].whole = whole;
        resources.desks.forEach((desk) => {
          byDay.slots[i].desks.add(desk);
        });

        centerByDay.slots[i].any = true;
        centerByDay.slots[i].whole = whole;
        resources.equipments.forEach((equipment) => {
          centerByDay.slots[i].equipments.add(equipment);
        });
      }
    }

    dateBegin = dateBegin.plus({ days: 1 });
  }
}

function cacheEquipmentsBookingsByTimeSlots(timeslots, booking, resources) {
  const center = resources.equipments[0].center;

  if (!timeslots.byCenter.has(center))
    timeslots.byCenter.set(center, { byDay: new Map() });
  const byCenter = timeslots.byCenter.get(center);

  let dateBegin = booking.dateStart;
  while (dateBegin < booking.dateEnd) {
    const dateFormatted = dateBegin.toFormat("yyyy-MM-dd");
    if (!byCenter.byDay.has(dateFormatted)) {
      const slots = [];
      for (let i = 0; i < 18; ++i)
        slots.push({ any: false, equipments: new Set() });
      byCenter.byDay.set(dateFormatted, {
        slots: slots,
      });
    }
    const byDay = byCenter.byDay.get(dateFormatted);

    const intervals = makeIntervals30(dateBegin);
    for (let i = 0; i < intervals.length; ++i) {
      if (intervals[i].overlaps(booking.interval)) {
        byDay.slots[i].any = true;
        resources.equipments.forEach((equipment) => {
          byDay.slots[i].equipments.add(equipment);
        });
      }
    }

    dateBegin = dateBegin.plus({ days: 1 });
  }
}

function cacheByTimeSlots(timeslots, booking) {
  if (booking.cancelled) return;

  const resources = { rooms: [], desks: [], equipments: [] };
  for (const resource of booking.resources) {
    if (resource.type === "desk") resources.desks.push(resource);
    else if (resource.type === "equipment") resources.equipments.push(resource);
    else if (resource.type === "room") resources.rooms.push(resource);
  }

  if (resources.rooms.length > 0) {
    cacheRoomBookingsByTimeSlots(timeslots, booking, resources);
  } else if (
    resources.rooms.length === 0 &&
    resources.desks.length === 0 &&
    resources.equipments.length > 0
  )
    cacheEquipmentsBookingsByTimeSlots(timeslots, booking, resources);
}

const mutations = {
  ON_BEFORE_LOAD_BOOKINGS(state) {
    state.timeslots = {
      byRoom: new Map(),
      byCenter: new Map(),
    };
  },
  ON_LOAD_NEW_BOOKING(state, booking) {
    cacheByTimeSlots(state.timeslots, booking);
  },
  ADD_NEW_BOOKING(state, booking) {
    state.bookings.push(booking);
    cacheByTimeSlots(state.timeslots, booking);
  },
  SET_TIMESLOTS(state, timeslots) {
    state.timeslots = timeslots;
  },
};

function makeInterval(date, startHour, startMinutes, endHour, endMinutes) {
  const Hmm = (hour, minutes) =>
    DateTime.fromObject({
      year: date.year,
      month: date.month,
      day: date.day,
      hour: hour,
      minutes: minutes,
    });

  return Interval.fromDateTimes(
    Hmm(startHour, startMinutes),
    Hmm(endHour, endMinutes)
  );
}

const getters = {
  getBookingById: (state) => (bookingId) => {
    return state.bookings.find((x) => x.id === bookingId);
  },
  getBookingsByUser: (state) => (user) => {
    return state.bookings.filter((x) => x.user.id === user.id);
  },
  getActiveBookingsByUser: (state) => (user) => {
    return state.bookings.filter((x) => x.user.id === user.id && !x.cancelled);
  },
  getBookingsByResource: (state) => (resource) => {
    return state.bookings.filter((x) => x.resources.includes(resource));
  },
  getBookingsByCenter: (state) => (center) => {
    return state.bookings.filter((x) => x.center === center);
  },
  getBookingsByCenterId: (state) => (centerId) => {
    return state.bookings.filter((booking) => booking.center.id === centerId);
  },
  isWholeRoomBooking: (state) => (booking) => {
    return _isWholeRoomBooking(booking);
  },

  // used by RoomsTimeGrid
  getRoomBookingsByTimeSlots: (state) => (room, date) => {
    try {
      return state.timeslots.byRoom
        .get(room)
        .byDay.get(date.toFormat("yyyy-MM-dd"));
    } catch (e) {
      return undefined;
    }
  },
  getCenterEquipmentsBookingsByTimeSlots: (state) => (center, date) => {
    try {
      return state.timeslots.byCenter
        .get(center)
        .byDay.get(date.toFormat("yyyy-MM-dd"));
    } catch (e) {
      return undefined;
    }
  },
  getRoomDesksBookedOnInterval: (state) => (room, desks, interval) => {
    const bookings = getters.getBookingsByResource(state)(room);
    let res = { desks: new Set(), bookings: new Map() };

    for (const booking of bookings) {
      if (booking.cancelled || !booking.resources) continue;
      const whole = _isWholeRoomBooking(booking);

      if (interval.overlaps(booking.interval)) {
        for (const desk of desks) {
          if (whole || booking.resources.includes(desk)) {
            res.desks.add(desk);
            if (!res.bookings.has(desk)) res.bookings.set(desk, new Set());
            res.bookings.get(desk).add(booking);
          }
        }
      }
    }

    return res;
  },
  getCenterEquipmentsBookedOnInterval:
    (state) => (center, equipments, interval) => {
      const bookings = getters.getBookingsByCenter(state)(center);
      let res = { equipments: new Set(), bookings: new Map() };

      for (const booking of bookings) {
        if (!booking.cancelled && interval.overlaps(booking.interval)) {
          for (const equipment of equipments) {
            if (booking.resources.includes(equipment)) {
              res.equipments.add(equipment);
              if (!res.bookings.has(equipment))
                res.bookings.set(equipment, new Set());
              res.bookings.get(equipment).add(booking);
            }
          }
        }
      }

      return res;
    },
  getActiveBookingsByCenterOnInterval: (state) => (center, interval) => {
    const bookings = getters.getBookingsByCenterId(state)(center.id);
    const res = [];
    for (const booking of bookings) {
      if (!booking.cancelled && interval.overlaps(booking.interval))
        res.push(booking);
    }
    return res;
  },

  getBookingsOnInterval: (state) => (centerId, interval) => {
    const bookings = getters.getBookingsByCenterId(state)(centerId);
    const res = [];
    for (const booking of bookings) {
      if (!booking.cancelled && interval.overlaps(booking.interval))
        res.push(booking);
    }
    return res;
  },
  bookedResourcesForDay: (state) => (date, centerId) => {
    const interval = makeInterval(date, 9, 0, 18, 0);
    const bookings = getters.getBookingsByCenterId(state)(centerId);
    const resources = new Set();
    for (const booking of bookings) {
      if (interval.overlaps(booking.interval)) {
        booking.resources.forEach((x) => resources.add(x));
      }
    }

    return resources;
  },

  bookedResourcesByTypeForDay: (state) => (date, centerId, resourceType) => {
    const interval = makeInterval(date, 9, 0, 18, 0);
    const bookings = getters.getBookingsByCenterId(state)(centerId);
    const resources = new Array();

    for (const booking of bookings) {
      if (interval.overlaps(booking.interval)) {
        for (const resource of booking.resources) {
          // Vérifiez si le type de ressource correspond au type spécifié
          if (resource.type === resourceType) {
            resources.push(resource);
          }
        }
      }
    }
    return resources;
  },
};

export default new CrudStoreBuilder()
  .setName("booking")
  .setService(bookingService)
  .setDependents(["user", "center", "room", "desk", "equipment"])
  .includeActions(actions)
  .includeMutations(mutations)
  .includeGetters(getters)
  .build();
